import { useRoute } from 'vue-router';
import { formatGradeName } from '@/lib/grades';
import type {
  NavigationItem,
  UseReportNavigationOptions,
} from '../types/navigation';
import {
  getIndicatorAppend,
  getGatewayAppend,
  getCriterionDisplayTitle,
} from './navigation-helpers';

export function buildGradeLevelNavigation(
  reportId: string,
  options: UseReportNavigationOptions,
): NavigationItem[] {
  const route = useRoute();
  const baseItems: NavigationItem[] = [];

  // Add overview section items first (like original GradeOverviewNav)
  baseItems.push(
    {
      title: 'Ratings Snapshot',
      value: 'ratings-snapshot',
      href: '#ratings-snapshot',
      route: {
        name: 'report-grade-overview',
        params: {
          id: reportId,
          grade: options.grade || '',
        },
      },
    },
    {
      title: 'Gateway Ratings Summary',
      value: 'gateway-ratings-summary',
      href: '#gateway-ratings-summary',
      route: {
        name: 'report-grade-overview',
        params: {
          id: reportId,
          grade: options.grade || '',
        },
      },
    },
    {
      title: 'Grade Level Overview Summary',
      value: 'grade-level-overview',
      href: '#grade-level-overview',
      route: {
        name: 'report-grade-overview',
        params: {
          id: reportId,
          grade: options.grade || '',
        },
      },
    },
  );

  // Add Publication Details if they exist
  if (options.gradeReport?.details && options.gradeReport.details.length > 0) {
    baseItems.push({
      title: 'Publication Details',
      value: 'publication-details',
      href: '#publication-details',
      route: {
        name: 'report-grade-overview',
        params: {
          id: reportId,
          grade: options.grade || '',
        },
      },
    });
  }

  // Build gateway items for grade level view
  if (options.gradeReport && options.grade) {
    const mainGateways = options.gradeReport.gateways || [];
    const overviewGateways = options.gradeReport.gateway_overviews || [];

    // Combine all gateways
    const gatewayMap = new Map();
    mainGateways.forEach((gateway: any) => {
      gatewayMap.set(gateway.slug, gateway);
    });
    overviewGateways.forEach((gateway: any) => {
      if (!gatewayMap.has(gateway.slug)) {
        gatewayMap.set(gateway.slug, gateway);
      }
    });

    const allGateways = Array.from(gatewayMap.values());

    // Group gateways by number
    const gatewaysByNumber = new Map();
    allGateways.forEach((gateway: any) => {
      const isMllGateway = gateway.slug === 'multilingual-learner-supports';

      let gatewayNumber: string;
      if (isMllGateway) {
        gatewayNumber = '4';
      } else {
        const match = gateway.slug.match(
          /gateway-(one|two|three|four|five|\d+)/,
        );
        if (match) {
          const numberPart = match[1];
          const numberMap: Record<string, string> = {
            one: '1',
            two: '2',
            three: '3',
            four: '4',
            five: '5',
          };
          gatewayNumber = numberMap[numberPart] || numberPart;
        } else {
          const labelMatch = gateway.label?.match(/Gateway\s+(\d+)/i);
          gatewayNumber = labelMatch ? labelMatch[1] : '1';
        }
      }

      if (!gatewaysByNumber.has(gatewayNumber)) {
        gatewaysByNumber.set(gatewayNumber, []);
      }
      gatewaysByNumber.get(gatewayNumber).push(gateway);
    });

    const gatewayItems: NavigationItem[] = [];
    let gateway1Found = false;

    // Helper function to get grade order from multiple possible sources
    const getGradeOrder = (gateway: any): number => {
      // First try the grade.order property
      if (gateway.grade?.order !== undefined) {
        return gateway.grade.order;
      }

      // Try to extract from gateway.grades array (new structure)
      if (
        gateway.grades &&
        Array.isArray(gateway.grades) &&
        gateway.grades.length > 0
      ) {
        const grade = gateway.grades[0];
        if (grade.order !== undefined) {
          return grade.order;
        }
        if (grade.shortcode) {
          // Handle Kindergarten
          if (grade.shortcode === 'K' || grade.shortcode === '0') {
            return 0;
          }
          // Handle numeric grades
          const numericMatch = grade.shortcode.match(/\d+/);
          if (numericMatch) {
            return parseInt(numericMatch[0]);
          }
        }
      }

      // Try to extract from grade name/shortcode
      const gradeName = gateway.grade?.name || gateway.grade?.shortcode || '';
      if (gradeName) {
        // Handle Kindergarten
        if (
          gradeName.toLowerCase().includes('kindergarten') ||
          gradeName === 'K' ||
          gradeName === '0'
        ) {
          return 0;
        }
        // Handle numeric grades
        const numericMatch = gradeName.match(/\d+/);
        if (numericMatch) {
          return parseInt(numericMatch[0]);
        }
      }

      // Try to extract from slug
      const slugMatch = gateway.slug?.match(/__(.+)$/);
      if (slugMatch) {
        const gradeSlug = slugMatch[1];
        if (gradeSlug.includes('kindergarten') || gradeSlug === 'k') {
          return 0;
        }
        const numericMatch = gradeSlug.match(/\d+/);
        if (numericMatch) {
          return parseInt(numericMatch[0]);
        }
      }

      // Try to extract from label
      const labelMatch = gateway.label?.match(/\((.*?)\)/);
      if (labelMatch) {
        const gradeLabel = labelMatch[1];
        if (
          gradeLabel.toLowerCase().includes('kindergarten') ||
          gradeLabel === 'K'
        ) {
          return 0;
        }
        const numericMatch = gradeLabel.match(/\d+/);
        if (numericMatch) {
          return parseInt(numericMatch[0]);
        }
      }

      // Default fallback
      return 999;
    };

    // Process each gateway number
    for (const [gatewayNumber, gateways] of gatewaysByNumber.entries()) {
      // Determine if this gateway should be open by default
      const isGateway1 = gatewayNumber === '1' || gatewayNumber === 'one';
      let gatewayDefaultOpen = false;

      if (isGateway1 && !gateway1Found) {
        gatewayDefaultOpen = true;
        gateway1Found = true;
        console.log(`🚪 Gateway ${gatewayNumber} will auto-open:`, {
          gatewayDefaultOpen,
          isGateway1,
        });
      }

      if (gateways.length > 1) {
        // Multiple gateways of the same number - create grade-specific children
        const sortedGateways = gateways.sort((a: any, b: any) => {
          const aGradeOrder = getGradeOrder(a);
          const bGradeOrder = getGradeOrder(b);
          return aGradeOrder - bGradeOrder;
        });

        const gatewayChildren = sortedGateways.map((gateway: any) => {
          let gradeTitle: string;
          let gradeSlug: string;

          if (gateway.grade?.name) {
            gradeTitle = formatGradeName(
              gateway.grade.name || gateway.grade.shortcode,
            );
            gradeSlug = gateway.grade.slug;
          } else {
            const gradeMatch = gateway.label?.match(/\((.*?)\)/);
            if (gradeMatch) {
              gradeTitle = gradeMatch[1];
              gradeSlug = gradeTitle.toLowerCase().replace(/\s+/g, '-');
            } else {
              const slugMatch = gateway.slug.match(/__(.+)$/);
              if (slugMatch) {
                gradeSlug = slugMatch[1];
                gradeTitle = formatGradeName(gradeSlug.replace(/-/g, ' '));
              } else {
                gradeTitle = `Grade ${gatewayNumber}`;
                gradeSlug = `grade-${gatewayNumber}`;
              }
            }
          }

          const gatewayWord =
            ['one', 'two', 'three', 'four', 'five'][
              parseInt(gatewayNumber) - 1
            ] || gatewayNumber;

          // Use the full gateway slug if it includes grade info
          const routeGateway = gateway.slug.includes('__')
            ? gateway.slug // Use full slug like "gateway-one__kindergarten"
            : gatewayWord === 'one'
              ? `gateway-${gatewayWord}__${gradeSlug}` // Add grade suffix only for Gateway 1
              : `gateway-${gatewayWord}`; // Use generic like "gateway-two" for others

          const criterionChildren: NavigationItem[] = [];
          const gatewayDetailSource = gateway.criterion;

          // Build criterion children for this specific grade-gateway
          if (gatewayDetailSource && Array.isArray(gatewayDetailSource)) {
            gatewayDetailSource.forEach((criterion: any, index: number) => {
              const indicatorChildren =
                criterion.indicators?.map((indicator: any) => {
                  const indicatorAppend = getIndicatorAppend(indicator);
                  return {
                    title: indicator.label,
                    value: `criterion-${gatewayWord}-${gradeSlug}-${index}-${indicator.label.toLowerCase().replace(/\s+/g, '-')}`,
                    append: indicatorAppend?.text || '',
                    appendClasses: indicatorAppend?.class || '',
                    route: {
                      name: 'report-gateway',
                      params: {
                        id: reportId,
                        grade: options.grade || '', // Use original grade parameter (e.g., kindergarten-to-second)
                        gateway: routeGateway, // Use specific gateway slug (e.g., gateway-one__kindergarten)
                      },
                    },
                    href: `#${routeGateway}-${criterion.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${indicator.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
                  };
                }) || [];

              criterionChildren.push({
                title: getCriterionDisplayTitle(criterion, gatewayDetailSource),
                value: `criterion-${gatewayWord}-${gradeSlug}-${index}`,
                isGroup: indicatorChildren.length > 0,
                indentChildren: true,
                children: indicatorChildren,
                // Auto-open the first criterion child if:
                // 1. This is the first criterion (index === 0) AND
                // 2. The gateway is auto-opened (gatewayDefaultOpen === true)
                defaultOpen: (() => {
                  const shouldOpen = index === 0 && gatewayDefaultOpen;
                  if (shouldOpen) {
                    console.log(`🔓 Auto-opening first criterion child:`, {
                      criterionTitle: getCriterionDisplayTitle(
                        criterion,
                        gatewayDetailSource,
                      ),
                      index,
                      gatewayDefaultOpen,
                      gatewayNumber,
                    });
                  }
                  return shouldOpen;
                })(),
              });
            });
          } else {
            // Fallback to gateway overviews
            const matchingOverview =
              options.gradeReport?.gateway_overviews?.find(
                (gw: any) => gw.slug === gateway.slug,
              );

            if (matchingOverview?.criterion_overviews) {
              matchingOverview.criterion_overviews.forEach(
                (criterion: any, index: number) => {
                  const indicatorChildren =
                    criterion.indicators?.map((indicator: any) => {
                      const indicatorAppend = getIndicatorAppend(indicator);
                      return {
                        title: indicator.label,
                        value: `criterion-${gatewayWord}-${gradeSlug}-${index}-${indicator.label.toLowerCase().replace(/\s+/g, '-')}`,
                        append: indicatorAppend?.text || '',
                        appendClasses: indicatorAppend?.class || '',
                        route: {
                          name: 'report-gateway',
                          params: {
                            id: reportId,
                            grade: options.grade || '', // Use original grade parameter (e.g., kindergarten-to-second)
                            gateway: routeGateway, // Use specific gateway slug (e.g., gateway-one__kindergarten)
                          },
                        },
                        href: `#${routeGateway}-${criterion.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${indicator.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
                      };
                    }) || [];

                  criterionChildren.push({
                    title: getCriterionDisplayTitle(criterion, null),
                    value: `criterion-${gatewayWord}-${gradeSlug}-${index}`,
                    isGroup: indicatorChildren.length > 0,
                    indentChildren: true,
                    children: indicatorChildren,
                    // Auto-open the first criterion child if the gateway is auto-opened
                    defaultOpen: (() => {
                      const shouldOpen = index === 0 && gatewayDefaultOpen;
                      if (shouldOpen) {
                        console.log(
                          `🔓 Auto-opening first criterion child (fallback):`,
                          {
                            criterionTitle: getCriterionDisplayTitle(
                              criterion,
                              null,
                            ),
                            index,
                            gatewayDefaultOpen,
                            gatewayNumber,
                          },
                        );
                      }
                      return shouldOpen;
                    })(),
                    route: {
                      name: 'report-gateway',
                      params: {
                        id: reportId,
                        grade: options.grade || '', // Use original grade parameter (e.g., kindergarten-to-second)
                        gateway: routeGateway, // Use specific gateway slug (e.g., gateway-one__kindergarten)
                      },
                    },
                    href: `#${routeGateway}-${criterion.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
                  });
                },
              );
            }
          }

          const gradeChildRoute = {
            name: 'report-gateway',
            params: {
              id: reportId,
              grade: options.grade || '', // Use original grade parameter (e.g., kindergarten-to-second)
              gateway: routeGateway, // Use specific gateway slug (e.g., gateway-one__kindergarten)
            },
          };

          const gradeChildItem = {
            title: gradeTitle,
            value: `gateway-${gatewayWord}-${gradeSlug}`,
            isGroup: criterionChildren.length > 0,
            indentChildren: true,
            children: criterionChildren,
            route: gradeChildRoute,
            href: `#${routeGateway}`,
          };

          return gradeChildItem;
        });

        gatewayItems.push({
          title: `Gateway ${gatewayNumber}`,
          value: `gateway-group-${gatewayNumber}`,
          isH4: true,
          isGroup: true,
          borderTop: true,
          defaultOpen: gatewayDefaultOpen,
          children: gatewayChildren,
          // No route property - this is just a group header
        });
      } else if (gateways.length === 1) {
        // Single gateway of this number - handle as before
        const gateway = gateways[0];

        if (gateway) {
          const isMllGateway = gateway.slug === 'multilingual-learner-supports';
          const gatewayWord = isMllGateway
            ? 'four'
            : ['one', 'two', 'three', 'four', 'five'][
                parseInt(gatewayNumber) - 1
              ] || gatewayNumber;

          // Use the full gateway slug if it includes grade info (like gateway-one__kindergarten)
          // Otherwise fall back to the generic gateway pattern
          const routeGateway = isMllGateway
            ? 'multilingual-learner-supports'
            : gateway.slug.includes('__')
              ? gateway.slug // Use full slug like "gateway-one__kindergarten"
              : `gateway-${gatewayWord}`; // Use generic like "gateway-one"

          // Extract the actual grade slug from the gateway data
          let actualGradeSlug = options.grade || '';
          if (gateway.grade?.slug) {
            actualGradeSlug = gateway.grade.slug;
          } else {
            // Try to extract from gateway slug pattern (like gateway-one__kindergarten)
            const slugMatch = gateway.slug.match(/__(.+)$/);
            if (slugMatch) {
              actualGradeSlug = slugMatch[1];
            }
          }

          // For API calls, we need the individual grade slug, not a range
          // If actualGradeSlug is still a range like "kindergarten-to-second",
          // extract the first grade from the range
          if (actualGradeSlug.includes('-to-')) {
            const rangeMatch = actualGradeSlug.match(/^([^-]+)-to-(.+)$/);
            if (rangeMatch) {
              actualGradeSlug = rangeMatch[1]; // Use the first grade in the range
            }
          }

          const children: NavigationItem[] = [];
          const gatewayDetailSource = gateway.criterion;

          // Build criterion children
          if (gatewayDetailSource && Array.isArray(gatewayDetailSource)) {
            gatewayDetailSource.forEach((criterion: any, index: number) => {
              const indicatorChildren =
                criterion.indicators?.map((indicator: any) => {
                  const indicatorAppend = getIndicatorAppend(indicator);
                  return {
                    title: indicator.label,
                    value: `criterion-${gatewayWord}-${actualGradeSlug}-${index}-${indicator.label.toLowerCase().replace(/\s+/g, '-')}`,
                    append: indicatorAppend?.text || '',
                    appendClasses: indicatorAppend?.class || '',
                    route: {
                      name: 'report-gateway',
                      params: {
                        id: reportId,
                        grade: options.grade || '', // Use original grade parameter (e.g., kindergarten-to-second)
                        gateway: routeGateway,
                      },
                    },
                    href: `#${routeGateway}-${criterion.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${indicator.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
                  };
                }) || [];

              children.push({
                title: getCriterionDisplayTitle(criterion, gatewayDetailSource),
                value: `criterion-${gatewayWord}-${actualGradeSlug}-${index}`,
                isGroup: indicatorChildren.length > 0,
                indentChildren: true,
                children: indicatorChildren,
                route: {
                  name: 'report-gateway',
                  params: {
                    id: reportId,
                    grade: options.grade || '', // Use original grade parameter (e.g., kindergarten-to-second)
                    gateway: routeGateway,
                  },
                },
                href: `#${routeGateway}-${criterion.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
              });
            });
          } else {
            // Fallback to gateway overviews
            const matchingOverview =
              options.gradeReport?.gateway_overviews?.find(
                (gw: any) => gw.slug === gateway.slug,
              );

            if (matchingOverview?.criterion_overviews) {
              matchingOverview.criterion_overviews.forEach(
                (criterion: any, index: number) => {
                  const indicatorChildren =
                    criterion.indicators?.map((indicator: any) => {
                      const indicatorAppend = getIndicatorAppend(indicator);
                      return {
                        title: indicator.label,
                        value: `criterion-${gatewayWord}-${actualGradeSlug}-${index}-${indicator.label.toLowerCase().replace(/\s+/g, '-')}`,
                        append: indicatorAppend?.text || '',
                        appendClasses: indicatorAppend?.class || '',
                        route: {
                          name: 'report-gateway',
                          params: {
                            id: reportId,
                            grade: options.grade || '', // Use original grade parameter (e.g., kindergarten-to-second)
                            gateway: routeGateway,
                          },
                        },
                        href: `#${routeGateway}-${criterion.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${indicator.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
                      };
                    }) || [];

                  children.push({
                    title: getCriterionDisplayTitle(criterion, null),
                    value: `criterion-${gatewayWord}-${actualGradeSlug}-${index}`,
                    isGroup: indicatorChildren.length > 0,
                    indentChildren: true,
                    children: indicatorChildren,
                    // Auto-open the first criterion child if the gateway is auto-opened
                    defaultOpen: (() => {
                      const shouldOpen = index === 0 && gatewayDefaultOpen;
                      if (shouldOpen) {
                        console.log(
                          `🔓 Auto-opening first criterion child (single gateway):`,
                          {
                            criterionTitle: getCriterionDisplayTitle(
                              criterion,
                              null,
                            ),
                            index,
                            gatewayDefaultOpen,
                            gatewayNumber,
                          },
                        );
                      }
                      return shouldOpen;
                    })(),
                    route: {
                      name: 'report-gateway',
                      params: {
                        id: reportId,
                        grade: options.grade || '', // Use original grade parameter (e.g., kindergarten-to-second)
                        gateway: routeGateway,
                      },
                    },
                    href: `#${routeGateway}-${criterion.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
                  });
                },
              );
            }
          }

          // Use the gatewayDefaultOpen determined at the start of the loop

          const gatewayAppend = getGatewayAppend(gateway);

          // Don't add route to gateway group title - it should only be expandable
          // The children will have the actual clickable links
          gatewayItems.push({
            title: isMllGateway
              ? 'Multilingual Learner Supports'
              : gateway.label || `Gateway ${gatewayNumber}`,
            value: `gateway-${gatewayWord}`,
            isH4: true,
            isGroup: true,
            borderTop: true,
            defaultOpen: gatewayDefaultOpen,
            // No route property - this is just a group header
            href: `#${routeGateway}`,
            children,
            append: gatewayAppend?.text || '',
            appendClasses: gatewayAppend?.class || '',
          });
        }
      }
    }

    baseItems.push(...gatewayItems);
  }

  return baseItems;
}
